import type { QueryClient } from '@tanstack/react-query';

import {
  DocumentSourceType,
  finalizeTransitionZipMultipartUploadApiTransitionsTransitionIdDocumentImportsUploadPost,
  registerTransitionZipMultipartUploadApiTransitionsTransitionIdDocumentImportsUploadPut,
  type TransitionDocumentCreateRequest,
} from '@/api/client';
import { buildJsonPayloadFileMeta } from '@/lib/uploads/adapters/build-json-payload-file-meta';
import { createDocumentImportMultipartHandlers } from '@/lib/uploads/adapters/create-document-import-multipart-handlers';
import type { S3MultipartHandlers } from '@/lib/uploads/adapters/create-s3-multipart-plugin';
import { inferDocumentFileType } from '@/lib/uploads/infer-document-file-type';
import type { EnqueueUploadParams } from '@/lib/uploads/types';
import type {
  UploadEnqueueEntryPoint,
  UploadEnqueueSource,
} from '@/lib/uploads/types';
import { showUploadSessionCompleteToast } from '@/lib/uploads/upload-complete-toast';
import type { UploadKindAdapter } from '@/lib/uploads/upload-kind-adapter';
import { requireMetaString } from '@/lib/uploads/utils/require-meta-string';
import { buildResourceKey } from '@/lib/uploads/utils/resource-key';
import { transitionQueryKey } from '@/pages/property-transitions/_hooks/use-transition';
import { transitionDocumentTasksQueryKey } from '@/pages/property-transitions/_hooks/use-transition-document-tasks';

export const TRANSITION_DOCUMENT_UPLOAD_KIND = 'transition-document';

export type TransitionUploadMeta = {
  transitionId: string;
  transitionName: string;
};

export const getTransitionResourceKey = (transitionId: string) =>
  buildResourceKey('transition', transitionId);

export const buildTransitionDocumentCreate = (
  transitionId: string,
  file: File
): TransitionDocumentCreateRequest => ({
  transition_id: transitionId,
  file_name: file.name,
  file_type: inferDocumentFileType(file),
  uploaded_at: new Date().toISOString(),
  source_type: DocumentSourceType.Secure_Upload,
});

export const buildTransitionEnqueueParams = (input: {
  transitionId: string;
  transitionName: string;
  returnPath: string;
  files: File[];
  source: UploadEnqueueSource;
  entryPoint: UploadEnqueueEntryPoint;
}): EnqueueUploadParams => ({
  kind: TRANSITION_DOCUMENT_UPLOAD_KIND,
  resourceKey: getTransitionResourceKey(input.transitionId),
  label: input.transitionName || 'Property transition',
  returnPath: input.returnPath,
  meta: {
    transitionId: input.transitionId,
    transitionName: input.transitionName,
  } satisfies TransitionUploadMeta,
  files: input.files,
  source: input.source,
  entryPoint: input.entryPoint,
});

export const getTransitionS3MultipartHandlers = (
  transitionId: string
): S3MultipartHandlers =>
  createDocumentImportMultipartHandlers(
    {
      register: (body) =>
        registerTransitionZipMultipartUploadApiTransitionsTransitionIdDocumentImportsUploadPut(
          { transitionId },
          body
        ),
      finalize: (body) =>
        finalizeTransitionZipMultipartUploadApiTransitionsTransitionIdDocumentImportsUploadPost(
          { transitionId },
          body
        ),
    },
    {
      register: ({ fileName, contentType, numberOfParts }) => ({
        property_id: transitionId,
        file_name: fileName,
        content_type: contentType,
        number_of_parts: numberOfParts,
        s3_upload_urls: [],
      }),
      finalize: ({ fileName, uploadId, parts, partUrlStore }) => ({
        property_id: transitionId,
        file_name: fileName,
        upload_id: uploadId,
        number_of_parts: parts.length,
        s3_upload_urls: parts.map((part) => ({
          upload_id: uploadId,
          part_number: Number(part.PartNumber),
          url: partUrlStore[Number(part.PartNumber)] ?? '',
          etag: part.ETag ?? null,
        })),
      }),
    }
  );

export const getTransitionXhrEndpoint = (transitionId: string) =>
  `/api/transitions/${transitionId}/documents`;

export const invalidateTransitionQueries = async (
  queryClient: QueryClient,
  transitionId: string
) => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: transitionQueryKey(transitionId),
    }),
    queryClient.invalidateQueries({
      queryKey: transitionDocumentTasksQueryKey(transitionId),
    }),
  ]);
};

export const getTransitionIdFromMeta = (meta: Record<string, unknown>) =>
  requireMetaString(meta, 'transitionId', 'transition id');

export const TRANSITION_DOCUMENTS_LABEL = 'Transition documents';

export const transitionDocumentUploadAdapter: UploadKindAdapter = {
  kind: TRANSITION_DOCUMENT_UPLOAD_KIND,
  featureLabel: 'Transitions',
  documentsLabel: TRANSITION_DOCUMENTS_LABEL,
  buildFileMeta: (file, sessionMeta) => {
    const transitionId = String(sessionMeta.transitionId ?? '');
    return buildJsonPayloadFileMeta({
      uploadKind: TRANSITION_DOCUMENT_UPLOAD_KIND,
      resourceKey: getTransitionResourceKey(transitionId),
      scopeMeta: { transitionId },
      file,
      xhrMetaField: 'transition_document',
      buildJsonPayload: () => buildTransitionDocumentCreate(transitionId, file),
    });
  },
  resolveMultipartHandlers: (fileMeta) =>
    getTransitionS3MultipartHandlers(getTransitionIdFromMeta(fileMeta)),
  resolveXhrEndpoint: (fileMeta) =>
    getTransitionXhrEndpoint(getTransitionIdFromMeta(fileMeta)),
  xhrAllowedMetaFields: ['transition_document'],
  extractHydratedSessionMeta: (fileMeta) => {
    const meta: Record<string, unknown> = {};

    if (
      fileMeta.transitionId != null &&
      fileMeta.transitionName == null &&
      typeof fileMeta.sessionLabel === 'string'
    ) {
      meta.transitionName = fileMeta.sessionLabel;
    }

    return meta;
  },
  onSessionComplete: async (session, { queryClient, outcome }) => {
    const transitionId = getTransitionIdFromMeta(session.meta);
    if (outcome.hasAnySuccess) {
      await invalidateTransitionQueries(queryClient, transitionId);
    }
    showUploadSessionCompleteToast(
      { documentsLabel: TRANSITION_DOCUMENTS_LABEL },
      { session, outcome }
    );
  },
};
